import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import i18n from 'i18n-js';
import moment from 'moment';

import { GlobalSearchItem, PartSearchItem, ProductionOrderSearchItem, RecipeSearchItem } from '@interfaces/search';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { SmallLoading } from '@components/loading';

import BlockHeader from './BlockHeader';
import LineButton from './LineButton';
import LineProgressButton from './LineProgressButton';
import { modalFieldBg } from '@v2/pages/common/constants/base.constants';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { SelectedLevel } from '@components/productionInformation/baseWithERP/types';

const PartBlock = ({
  C,
  item,
  partId,
  setProductionOrder,
  setRecipe,
  part,
  setPart,
  setSelectedLevel,
  handleItemInfoPress,
  generateImageURL,
  productionOrdersSearch,
  recipesSearch,
  partsSearch,
  historyPool,
  setHistoryPool,
  isBatch,
  hideMoveHistoryBackButton,
  scale = initialScale,
  font = initialFont,
}: {
  C: any;
  ncFileName?: string;
  partId?: string;
  setProductionOrder: Dispatch<SetStateAction<ProductionOrderSearchItem | undefined>>;
  setRecipe: Dispatch<SetStateAction<RecipeSearchItem | undefined>>;
  part: GlobalSearchItem | undefined | null;
  setPart: Dispatch<SetStateAction<GlobalSearchItem | undefined | null>>;
  setSelectedLevel: Dispatch<SetStateAction<SelectedLevel | null>>;
  item: GlobalSearchItem | null;
  handleItemInfoPress: any;
  generateImageURL: any;
  productionOrdersSearch: any;
  recipesSearch: any;
  partsSearch: any;
  historyPool: any;
  setHistoryPool: any;
  isBatch: boolean;
  hideMoveHistoryBackButton?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [productionOrders, setProductionOrders] = useState<ProductionOrderSearchItem[]>([]);
  const [recipes, setRecipes] = useState<RecipeSearchItem[]>([]);
  const [internalPart, setInternalPart] = useState<PartSearchItem | null>(null);

  const oDataFilterWithItem = `PartId eq ${item?.itemId}`;
  const oDataFilterWithoutItem = `PartId eq ${partId}`;
  const filter = item ? oDataFilterWithItem : oDataFilterWithoutItem;

  const handleSelectProductionOrder = (productionOrder: ProductionOrderSearchItem) => {
    setHistoryPool([...historyPool, { resetProductionOrder: true, resetLevelTo: SelectedLevel.Part }]);
    setSelectedLevel(SelectedLevel.ProductionOrder);
    console.log(productionOrder, 'productionOrder');
    setProductionOrder(productionOrder);
    setRecipe(undefined);
    setPart(item);
  };

  const handleSelectRecipe = async (recipe: RecipeSearchItem) => {
    setHistoryPool([...historyPool, { resetRecipe: true, resetLevelTo: SelectedLevel.Part }]);
    setSelectedLevel(SelectedLevel.Recipe);
    setRecipe(recipe);
    setProductionOrder(undefined);
    setPart(item);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      if (!item && partId) {
        const resParts = await partsSearch({
          data: {
            search: '',
            top: 100,
            skip: 0,
          },
          filter: { oDataFilter: oDataFilterWithoutItem },
          select: null,
          resetHistory: true,
          ignoreHistory: true,
        });
        if (resParts?.searchResult[0]) {
          setInternalPart(resParts.searchResult[0]);
        } else {
          setIsError(true);
        }
      }

      if (!isBatch) {
        const resRecipes = await recipesSearch(
          {
            search: '',
            top: 100,
            skip: 0,
          },
          { oDataFilter: filter }
        );
        if (resRecipes?.searchResult) {
          setRecipes(resRecipes.searchResult);
        } else {
          setIsError(true);
        }
      }

      const resProductionOrders = await productionOrdersSearch({
        data: {
          search: '',
          top: 100,
          skip: 0,
        },
        filter: {
          oDataFilter: filter,
          orderBy: ['ScheduledStartDate desc'],
        },
        resetHistory: true,
        ignoreHistory: true,
      });
      if (resProductionOrders?.searchResult) {
        setProductionOrders(resProductionOrders.searchResult);
      } else {
        setIsError(true);
      }

      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <SmallLoading />
      </View>
    );
  }
  return (
    <>
      <BlockHeader
        {...{
          scale,
          font,
          C,
          documentData: item?.documentData || { contentHash: internalPart?.imageHash, isImage: true, ...internalPart?.imageData },
          itemDescription: item?.itemDescription || internalPart?.partName,
          itemMemo: item?.itemMemo || internalPart?.description,
          itemType: 'Part',
          handleItemInfoPress,
          generateImageURL,
          hideMoveHistoryBackButton,
          onDeletePress: () => {
            setHistoryPool([]);
            setSelectedLevel(null);
            setProductionOrder(undefined);
            setRecipe(undefined);
            setPart(undefined);
          },
        }}
      />
      {!isBatch && (
        <View style={{ marginBottom: scale(40) }}>
          <Text numberOfLines={1} style={[font['Title 2'], { color: C('Label/Light/Primary'), marginBottom: scale(10), paddingLeft: scale(18) }]}>
            {i18n.t('Select Recipe')}
          </Text>
          {recipes.length ? (
            <FlashList
              data={recipes}
              keyExtractor={(item) => item.recipeId.toString()}
              contentContainerStyle={{ paddingHorizontal: scale(18) }}
              renderItem={({ item, index }) => {
                return (
                  <LineButton
                    {...{
                      scale,
                      font,
                      C,
                      onPress: () => handleSelectRecipe(item),
                      height: 66,
                      bg: modalFieldBg,
                      title: item.recipeNumber,
                      text: `Created on ${moment(item.createdOn).format('D MMM YYYY')} by ${item.createdBy}`,
                      shadow: false,
                      borderBottom: index !== recipes.length - 1,
                      borderRadiusTop: index === 0,
                      borderRadiusBottom: index === recipes.length - 1,
                    }}
                  />
                );
              }}
            />
          ) : (
            <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), paddingLeft: scale(18) }]}>
              {i18n.t('Recipes not found')}
            </Text>
          )}
        </View>
      )}
      <View style={{ flex: 1, paddingHorizontal: scale(18) }}>
        <Text numberOfLines={1} style={[font['Title 2'], { color: C('Label/Light/Primary'), marginBottom: scale(10), minHeight: scale(25) }]}>
          {i18n.t(`${isBatch ? '' : 'Or '}Select Production Order`)}
        </Text>
        {productionOrders.length ? (
          <FlashList
            data={productionOrders}
            keyExtractor={(item) => item.productionOrderId.toString()}
            contentContainerStyle={{ paddingBottom: scale(15) }}
            renderItem={({ item, index }) => {
              return (
                <LineProgressButton
                  {...{
                    scale,
                    font,
                    C,
                    onPress: () => handleSelectProductionOrder(item),
                    progress: item?.percentageComplete,
                    height: 66,
                    title: item?.productionOrderNumber,
                    text: item?.scheduledEndDate && `${moment(item.scheduledEndDate).format('D MMM YYYY')}`,
                    additionalBtnIcon: 'info',
                    status: item?.isActive ? 'running' : 'completed',
                    shadow: false,
                    borderBottom: index !== productionOrders.length - 1,
                    borderRadiusTop: index === 0,
                    borderRadiusBottom: index === productionOrders.length - 1,
                    bg: modalFieldBg,
                  }}
                />
              );
            }}
          />
        ) : (
          <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary') }]}>
            {i18n.t('Production orders not found')}
          </Text>
        )}
        <Text style={[font['Caption 1'], { color: C('Label/Light/Secondary'), marginVertical: scale(10) }]}>
          {i18n.t(
            'You can add only Production Orders without Recipe to the Link Library. If a Production Order has a Recipe, add the Recipe instead.'
          )}
        </Text>
      </View>
    </>
  );
};

export default PartBlock;
